import {
    FetchState,
    RolePermissionsState,
    RolePermissionsResponse,
    RolePermissionsCollection,
    Permission,
    UserPermission
} from "@/shared/types";
import * as UserService from "@/shared/api/userService";
import * as storeHelper from "@/shared/storeHelper";
import _ from "lodash";
import { defineStore } from "pinia";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useUserStore } from "@/shared/store/user";

export const initialState: RolePermissionsState = {
    fetchState: FetchState.Uninitialized,
    partnerPermissions: {},
    employerPermissions: {},
    userPermissions: []
};

export const useRolePermissionsStore = defineStore("rolePermissions", {
    persist: {
        storage: localStorage,
        paths: storeHelper.getStoragePaths("rolePermissions"),
    },
    state: () => (_.cloneDeep(initialState)),
    getters: {
        userHasPermission () {
            return (permission: Permission) => {
                const userStore = useUserStore();
                const employerId: number = userStore.employerId;
                const partnerId: number = userStore.partnerId;
                let permissions: Permission[] = null;

                // if we are in admin portal, treat as if had permission
                if (userStore.site === "ADMIN") {
                    return true;
                }

                // if we have employer permissions, check those
                // otherwise, check partner permissions
                const erPerms = this.employerPermissions[employerId] || [];
                const prPerms = this.partnerPermissions[partnerId] || [];
                permissions = prPerms.concat(...erPerms);

                // if we have no employer or partner permissions, return false
                if (!permissions) {
                    return false;
                }
                return _.includes(permissions, permission);
            };
        },
        userHasPartnerPermission () {
            return (permission: Permission, partnerId: number) => {
                const permissions = this.partnerPermissions[partnerId];

                // if we have no partner permissions, return false
                if (!permissions) {
                    return false;
                }

                return _.includes(permissions, permission);
            };
        },
        userHasAdminPermission () {
            const featureFlagStore = useFeatureFlagStore();
            return (permission: UserPermission) => {
                let permissions: UserPermission[] = null;
                // return true if permissions feature flag is turned off
                if (!featureFlagStore.flags.hsa4475allowRolePermissions) {
                    return true;
                }


                permissions = this.userPermissions;
                // if we have no permissions, return false
                if (!permissions) {
                    return false;
                }

                const hasPermission = permissions.some(x => x === permission);
                return hasPermission;
            };
        },
        doesUserHavePermission () {
            const featureFlagStore = useFeatureFlagStore();
            return (permission: UserPermission) => {
                let permissions: UserPermission[] = null;

                permissions = this.userPermissions;
                // if we have no permissions, return false
                if (!permissions) {
                    return false;
                }

                const hasPermission = permissions.some(x => x === permission);
                return hasPermission;
            };
        }
    },
    actions: {
        setFetchState (fetchState: FetchState) {
            this.fetchState = fetchState;
        },
        setRolePermissions (response: RolePermissionsResponse) {
            const formattedEmployerPermissions: RolePermissionsCollection = {};
            response.employerPermissions.forEach(x => {
                formattedEmployerPermissions[x.employerId] = x.permissions;
            });

            const formattedPartnerPermissions: RolePermissionsCollection = {};
            response.partnerPermissions.forEach(x => {
                formattedPartnerPermissions[x.partnerId] = x.permissions;
            });

            this.employerPermissions = formattedEmployerPermissions;
            this.partnerPermissions = formattedPartnerPermissions;
        },
        setUserPermissions (response: any) {
            this.userPermissions = response.responseData;
        },
        getRolePermissions (): Promise<void> {
            if (this.fetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setFetchState(FetchState.Loading);

            UserService.getUserPermissions().then(resp => {
                this.setUserPermissions(resp.data);
                this.setFetchState(FetchState.Loaded);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });

            return UserService.getRolePermissions().then(resp => {
                this.setRolePermissions(resp.data);
                this.setFetchState(FetchState.Loaded);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        getUserPermissions (): Promise<void> {
            // return if permissions feature flag is turned off
            const featureFlagStore = useFeatureFlagStore();
            if (!featureFlagStore.flags.hsa4475allowRolePermissions) {
                return Promise.resolve();
            }

            if (this.fetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setFetchState(FetchState.Loading);

            return UserService.getUserPermissions().then(resp => {
                this.setUserPermissions(resp.data);
                this.setFetchState(FetchState.Loaded);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        }
    }
});
